import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useSuggestions } from "@/context/SuggestionsContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useI18n } from "@/context/I18nContext";
import { CrisisResources } from "@/components/CrisisResources";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandMark } from "@/components/BrandMark";
import { mediaUrl } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, MessageCircleHeart, BookOpen, HeartPulse, Sparkles, Target,
  ListChecks, TreeDeciduous, Users, Stethoscope, LifeBuoy, ScrollText, Brain,
  ShieldCheck, Settings as SettingsIcon, Sun, Moon, Contrast, LogOut, Menu, X, HandHeart, HelpCircle,
  Lock, CreditCard, Quote,
} from "lucide-react";

const NAV = [
  { to: "/app", tkey: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/companion", tkey: "companion", label: "Companion", icon: MessageCircleHeart },
  { to: "/app/checkin", tkey: "checkin", label: "Check-in", icon: HeartPulse },
  { to: "/app/journal", tkey: "journal", label: "Journal", icon: BookOpen, premium: true },
  { to: "/app/insights", tkey: "insights", label: "Insights", icon: Sparkles, premium: true },
  { to: "/app/habits", tkey: "habits", label: "Habits", icon: ListChecks, premium: true },
  { to: "/app/goals", tkey: "goals", label: "Goals", icon: Target, premium: true },
  { to: "/app/favorites", tkey: "favorites", label: "Favorites", icon: Quote },
  { to: "/app/lifestory", tkey: "lifestory", label: "Life Story", icon: ScrollText, premium: true },
  { to: "/app/memories", tkey: "memories", label: "Memories", icon: Brain, premium: true },
  { to: "/app/growth", tkey: "growth", label: "Growth", icon: TreeDeciduous, premium: true },
  { to: "/app/community", tkey: "community", label: "Community", icon: Users, premium: true },
  { to: "/app/professionals", tkey: "findHelp", label: "Find Help", icon: Stethoscope },
  { to: "/app/resources", tkey: "resources", label: "Resources", icon: LifeBuoy },
  { to: "/app/circle", tkey: "supportCircle", label: "Support Circle", icon: HandHeart, premium: true },
  { to: "/app/guide", tkey: "guide", label: "Guide", icon: HelpCircle },
  { to: "/app/settings", tkey: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const { count: suggestionCount } = useSuggestions();
  const { unread: notifUnread } = useNotifications();
  const { sub, premium } = useSubscription();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [crisis, setCrisis] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const ThemeIcon = theme === "dark" ? Moon : theme === "hc" ? Contrast : Sun;
  const locked = (item) => item.premium && !premium;

  const nav = [...NAV];
  if (user?.role === "admin") nav.push({ to: "/app/admin", tkey: "admin", label: "Admin", icon: ShieldCheck });

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-6 py-6">
        <BrandMark className="h-9 w-9" animate />
        <span className="font-serif text-xl font-semibold tracking-tight">EverMind</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 no-scrollbar pb-4">
        {nav.map((item) => (
          <NavLink
            key={item.to} to={item.to} end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm transition-colors duration-300 ${
                isActive ? "bg-primary/15 text-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            {t(`nav.${item.tkey}`, item.label)}
            {item.to === "/app/memories" && suggestionCount > 0 && !locked(item) && (
              <span data-testid="memory-badge"
                    className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                {suggestionCount}
              </span>
            )}
            {item.to === "/app/community" && notifUnread > 0 && !locked(item) && (
              <span data-testid="community-nav-badge"
                    className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                {notifUnread > 9 ? "9+" : notifUnread}
              </span>
            )}
            {locked(item) && (
              <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground/70" strokeWidth={1.5} data-testid={`nav-lock-${item.tkey}`} />
            )}
          </NavLink>
        ))}
      </nav>
      {!premium && !sub?.is_admin && (
        <div className="px-3 pb-2">
          <button
            data-testid="upgrade-banner"
            onClick={() => { setMobileOpen(false); navigate("/app/subscription"); }}
            className="w-full rounded-2xl border border-primary/30 bg-primary/10 p-3 text-left transition-colors hover:bg-primary/15"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {sub?.status === "trialing" ? `${sub.trial_days_left} ${sub.trial_days_left === 1 ? t("sub.day") : t("sub.days")} ${t("side.leftInTrial")}` : t("side.unlock")}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {sub?.intro_used ? t("side.subUpsell") : t("side.introUpsell")}
            </p>
          </button>
        </div>
      )}
      <div className="border-t border-border/60 p-4">
        <div className="flex items-center gap-3 rounded-2xl px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-sm font-medium overflow-hidden">
            {user?.picture ? <img src={mediaUrl(user.picture)} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.name || "You"}</div>
            <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          </div>
        </div>
        <div className="mt-2 px-2">
          <LanguageSwitcher compact testid="sidebar-language-switcher" />
        </div>
        <NavLink
          to="/app/subscription" onClick={() => setMobileOpen(false)} data-testid="nav-subscription"
          className="mt-2 flex items-center gap-2 rounded-full px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          <CreditCard className="h-4 w-4" strokeWidth={1.5} /> {t("side.plan")}
        </NavLink>
        <div className="mt-2 flex gap-2">
          <Button variant="ghost" size="sm" onClick={cycleTheme} data-testid="theme-toggle-button"
                  className="flex-1 rounded-full justify-start gap-2 text-muted-foreground">
            <ThemeIcon className="h-4 w-4" strokeWidth={1.5} /> {t("common.theme", "Theme")}
          </Button>
          <Button variant="ghost" size="sm" data-testid="logout-button"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="rounded-full text-muted-foreground">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="grain min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border/60 bg-card/40 backdrop-blur-xl lg:block">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border/40 bg-background/70 px-4 py-3 backdrop-blur-2xl lg:hidden">
        <div className="flex items-center gap-2">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
          <span className="font-serif text-lg font-semibold">EverMind</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell onNavigate={() => setMobileOpen(false)} />
          <Button size="sm" variant="destructive" className="rounded-full" data-testid="crisis-button-mobile" onClick={() => setCrisis(true)}>
            <LifeBuoy className="mr-1 h-4 w-4" strokeWidth={1.5} /> {t("common.help", "Help")}
          </Button>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-card shadow-2xl">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 z-10" aria-label="Close menu">
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
          <div className="mb-6 hidden items-center justify-end gap-2 lg:flex">
            <NotificationBell />
            <Button variant="outline" size="sm" className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    data-testid="crisis-button" onClick={() => setCrisis(true)}>
              <LifeBuoy className="mr-2 h-4 w-4" strokeWidth={1.5} /> {t("common.needHelp", "Need help now?")}
            </Button>
          </div>
          <Outlet />
        </div>
      </div>

      <CrisisResources open={crisis} onClose={() => setCrisis(false)} />
    </div>
  );
}
